import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle, XCircle, Copy, ArrowRight, ArrowLeft, CircleNotch } from "@phosphor-icons/react";
import { SecretInput } from "@/components/SecretInput";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const WEBHOOK_URL = `${BACKEND}/api/whatsapp/webhook`;

const inputCls = "bg-[#0A0A0C] border-[#27272A] mt-1 font-mono-jb text-xs";

function CopyRow({ value, testId }) {
  return (
    <div className="flex gap-2 mt-1">
      <Input readOnly value={value} className={inputCls + " flex-1 mt-0"} data-testid={testId} />
      <Button size="sm" variant="outline" className="border-[#27272A] bg-[#18181B]" onClick={() => { navigator.clipboard.writeText(value); toast.success("Copiado"); }}>
        <Copy size={14} />
      </Button>
    </div>
  );
}

export const ConnectWizard = ({ open, onOpenChange, onCreated }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    label: "", display_phone: "", phone_number_id: "", business_account_id: "",
    access_token: "", verify_token: `vtok_${Math.random().toString(36).slice(2, 10)}`,
    app_secret: "", enabled: true, is_default: false,
  });
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const reset = () => {
    setStep(1);
    setResult(null);
    setForm({ label: "", display_phone: "", phone_number_id: "", business_account_id: "", access_token: "", verify_token: `vtok_${Math.random().toString(36).slice(2, 10)}`, app_secret: "", enabled: true, is_default: false });
  };

  const close = (v) => { onOpenChange(v); if (!v) reset(); };

  const createAndTest = async () => {
    if (!form.label) return toast.error("La etiqueta es requerida");
    setTesting(true);
    setResult(null);
    try {
      const { data: num } = await api.post("/whatsapp-numbers", form);
      const { data: test } = await api.post(`/whatsapp-numbers/${num.id}/test`);
      setResult(test);
      onCreated();
      if (test.success) toast.success("Numero conectado y verificado");
      else toast.warning("Numero creado, pero revisa el diagnostico");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear el numero");
    } finally { setTesting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[#121214] border-[#27272A] text-white max-w-lg" data-testid="connect-wizard">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Conectar numero WhatsApp</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-1 flex-1 rounded-full ${s <= step ? "bg-[#5E6AD2]" : "bg-[#27272A]"}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-3" data-testid="wizard-step-1">
            <p className="text-xs text-[#A1A1AA]">
              Paso 1 · Credenciales. En <a href="https://developers.facebook.com" target="_blank" rel="noreferrer" className="text-[#9EA9FF] hover:underline">developers.facebook.com</a> abre tu app → <span className="text-white">WhatsApp → API Setup</span>. Ahi encuentras el Phone Number ID, WABA ID y puedes generar el token.
            </p>
            <div><Label>Etiqueta *</Label><Input value={form.label} onChange={set("label")} placeholder="Ventas LATAM" className="bg-[#0A0A0C] border-[#27272A] mt-1" data-testid="wizard-label" /></div>
            <div><Label>Phone Number ID</Label><Input value={form.phone_number_id} onChange={set("phone_number_id")} className={inputCls} data-testid="wizard-phone-id" /></div>
            <div><Label>Business Account ID (WABA)</Label><Input value={form.business_account_id} onChange={set("business_account_id")} className={inputCls} /></div>
            <div><Label>Access Token</Label><SecretInput value={form.access_token} onChange={set("access_token")} className={inputCls} testId="wizard-token" /></div>
            <div><Label>Telefono visible</Label><Input value={form.display_phone} onChange={set("display_phone")} placeholder="+52 55 1234 5678" className={inputCls} /></div>
            <Button onClick={() => setStep(2)} className="w-full bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid="wizard-next-1">
              Siguiente <ArrowRight size={14} className="ml-2" />
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="wizard-step-2">
            <p className="text-xs text-[#A1A1AA]">
              Paso 2 · Webhook. En tu app Meta ve a <span className="text-white">WhatsApp → Configuration → Webhooks</span>, pega esta URL y el Verify Token, y suscribete al campo <span className="font-mono-jb text-white">messages</span>.
            </p>
            <div><Label>Callback URL</Label><CopyRow value={WEBHOOK_URL} testId="wizard-webhook-url" /></div>
            <div><Label>Verify Token</Label><CopyRow value={form.verify_token} testId="wizard-verify-token" /></div>
            <div>
              <Label>App Secret (HMAC, opcional)</Label>
              <SecretInput value={form.app_secret} onChange={set("app_secret")} placeholder="Meta App Dashboard → Configuracion → Basica" className={inputCls} testId="wizard-app-secret" />
              <p className="text-[10px] text-[#71717A] mt-1">Recomendado en produccion: valida la firma de Meta y rechaza webhooks falsos.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1 border-[#27272A] bg-[#18181B]"><ArrowLeft size={14} className="mr-2" /> Atras</Button>
              <Button onClick={() => setStep(3)} className="flex-1 bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid="wizard-next-2">Siguiente <ArrowRight size={14} className="ml-2" /></Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3" data-testid="wizard-step-3">
            <p className="text-xs text-[#A1A1AA]">Paso 3 · Crear y verificar la conexion en vivo contra la API de Meta.</p>
            {!result && (
              <Button onClick={createAndTest} disabled={testing} className="w-full bg-[#5E6AD2] hover:bg-[#4B55C3] h-11" data-testid="wizard-create-test">
                {testing ? (<><CircleNotch size={16} className="mr-2 animate-spin" /> Verificando con Meta...</>) : "Crear numero y probar conexion"}
              </Button>
            )}
            {result && (
              <div className="space-y-2" data-testid="wizard-test-results">
                {result.checks.map((c, i) => (
                  <div key={i} className="flex items-start gap-2.5 p-2.5 rounded-md bg-[#0A0A0C] border border-[#18181B]">
                    {c.ok ? <CheckCircle size={16} weight="fill" className="text-[#10B981] mt-0.5 shrink-0" /> : <XCircle size={16} weight="fill" className="text-[#F59E0B] mt-0.5 shrink-0" />}
                    <div>
                      <div className="text-xs font-medium">{c.check}</div>
                      <div className="text-[10px] text-[#71717A]">{c.detail}</div>
                    </div>
                  </div>
                ))}
                <Button onClick={() => close(false)} className="w-full bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid="wizard-finish">Finalizar</Button>
              </div>
            )}
            {!result && (
              <Button variant="outline" onClick={() => setStep(2)} className="w-full border-[#27272A] bg-[#18181B]"><ArrowLeft size={14} className="mr-2" /> Atras</Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
